import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
// Eager-load landing routes — they're the ad entry points, so any lazy
// chunk waterfall delays LCP (the H1) by hundreds of ms. ThankYou/NotFound
// stay lazy since they're post-conversion / fallback.
import Index from "./pages/Index.tsx";
import May132026 from "./pages/May132026.tsx";
import May202026 from "./pages/May202026.tsx";
import May292026 from "./pages/May292026.tsx";
import June22026 from "./pages/June22026.tsx";
import June252026 from "./pages/June252026.tsx";
import July162026 from "./pages/July162026.tsx";
import July242026 from "./pages/July242026.tsx";
import Aug42026 from "./pages/Aug42026.tsx";
import Aug122026 from "./pages/Aug122026.tsx";
import Sep102026 from "./pages/Sep102026.tsx";
import Sep222026 from "./pages/Sep222026.tsx";
import Sep232026 from "./pages/Sep232026.tsx";
import Oct152026 from "./pages/Oct152026.tsx";
import Oct222026 from "./pages/Oct222026.tsx";
import Oct282026 from "./pages/Oct282026.tsx";
import Nov102026 from "./pages/Nov102026.tsx";
import Nov192026 from "./pages/Nov192026.tsx";
import Dec92026 from "./pages/Dec92026.tsx";
import Dec172026 from "./pages/Dec172026.tsx";
import OnDemandWebinar from "./pages/OnDemandWebinar.tsx";
import OnDemandWebinarEmbed from "./pages/OnDemandWebinarEmbed.tsx";
import WebinarEmbed from "./pages/WebinarEmbed.tsx";
const ThankYou = lazy(() => import("./pages/ThankYou.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    dataLayer?: Record<string, any>[];
    lm_navi?: (...args: any[]) => void;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — cached data stays fresh, no refetch on remount
      gcTime: 30 * 60 * 1000, // 30 min — keep cache around for repeat visits
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const MetaPixelTracker = () => {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!window.fbq) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;

      if (location.pathname.endsWith("/ty")) {
        window.fbq("track", "Lead");
      }

      return;
    }

    window.fbq("track", "PageView");

    if (location.pathname.endsWith("/ty")) {
      window.fbq("track", "Lead");
    }
  }, [location.pathname]);

  // Push virtual pageview to GTM dataLayer for SPA navigation
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "virtualPageview",
      pagePath: location.pathname,
      pageUrl: window.location.href,
    });

    // Lawmatics pixel pageload on SPA navigation
    if (window.lm_navi) {
      window.lm_navi("event", "pageload");
    }
  }, [location.pathname]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Sonner />
    <BrowserRouter>
        <MetaPixelTracker />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<June252026 />} />
            <Route path="/may-13-2026" element={<May132026 />} />
            <Route path="/may-13-2026/ty" element={<ThankYou formKey="may13" />} />
            <Route path="/may-20-2026" element={<May202026 />} />
            <Route path="/may-20-2026/ty" element={<ThankYou formKey="may20" />} />
            <Route path="/may-29-2026" element={<May292026 />} />
            <Route path="/may-29-2026/ty" element={<ThankYou formKey="may29" />} />
            <Route path="/june-2-2026" element={<June22026 />} />
            <Route path="/june-2-2026/ty" element={<ThankYou formKey="june2" />} />
            <Route path="/june-25-2026" element={<June252026 />} />
            <Route path="/june-25-2026/ty" element={<ThankYou formKey="june25" />} />
            <Route path="/july-16-2026" element={<July162026 />} />
            <Route path="/july-16-2026/ty" element={<ThankYou formKey="july16" />} />
            <Route path="/july-24-2026" element={<July242026 />} />
            <Route path="/july-24-2026/ty" element={<ThankYou formKey="july24" />} />
            <Route path="/aug-4-2026" element={<Aug42026 />} />
            <Route path="/aug-4-2026/ty" element={<ThankYou formKey="aug4" />} />
            <Route path="/aug-12-2026" element={<Aug122026 />} />
            <Route path="/aug-12-2026/ty" element={<ThankYou formKey="aug12" />} />
            <Route path="/september-10-2026" element={<Sep102026 />} />
            <Route path="/september-10-2026/ty" element={<ThankYou formKey="sep10" />} />
            <Route path="/september-22-2026" element={<Sep222026 />} />
            <Route path="/september-22-2026/ty" element={<ThankYou formKey="sep22" />} />
            <Route path="/september-23-2026" element={<Sep232026 />} />
            <Route path="/september-23-2026/ty" element={<ThankYou formKey="sep23" />} />
<Route path="/october-15-2026" element={<Oct152026 />} />
            <Route path="/october-15-2026/ty" element={<ThankYou formKey="oct15" />} />
            <Route path="/october-22-2026" element={<Oct222026 />} />
            <Route path="/october-22-2026/embed" element={<WebinarEmbed formKey="oct22" />} />
            <Route path="/october-22-2026/ty" element={<ThankYou formKey="oct22" />} />
            <Route path="/october-28-2026" element={<Oct282026 />} />
            <Route path="/october-28-2026/ty" element={<ThankYou formKey="oct28" />} />
            <Route path="/november-10-2026" element={<Nov102026 />} />
            <Route path="/november-10-2026/ty" element={<ThankYou formKey="nov10" />} />
            <Route path="/november-19-2026" element={<Nov192026 />} />
            <Route path="/november-19-2026/embed" element={<WebinarEmbed formKey="nov19" />} />
            <Route path="/november-19-2026/ty" element={<ThankYou formKey="nov19" />} />
            <Route path="/december-9-2026" element={<Dec92026 />} />
            <Route path="/december-9-2026/ty" element={<ThankYou formKey="dec9" />} />
            <Route path="/december-17-2026" element={<Dec172026 />} />
            <Route path="/december-17-2026/embed" element={<WebinarEmbed formKey="dec17" />} />
            <Route path="/december-17-2026/ty" element={<ThankYou formKey="dec17" />} />
            <Route path="/on-demand-webinar" element={<OnDemandWebinar />} />
            <Route path="/on-demand-webinar/" element={<OnDemandWebinar />} />
            <Route path="/on-demand-webinar/embed" element={<OnDemandWebinarEmbed />} />
            <Route path="/on-demand-webinar/embed/" element={<OnDemandWebinarEmbed />} />
            <Route path="/on-demand-webinar/ty" element={<ThankYou formKey="odw" />} />
            <Route path="/on-demand-webinar/ty/" element={<ThankYou formKey="odw" />} />
            <Route path="/aug-27-2026" element={<Navigate to="/september-10-2026" replace />} />
            <Route path="/aug-27-2026/ty" element={<Navigate to="/september-10-2026/ty" replace />} />



            <Route path="/ty" element={<ThankYou />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
